import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, XCircle, Package, AlertTriangle, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

export function ReturnsPage() {
    const { t, language } = useLanguage();
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [dialogType, setDialogType] = useState(null); // 'approve' or 'reject'
    const [notes, setNotes] = useState('');
    const [damageReason, setDamageReason] = useState('');
    const [processing, setProcessing] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchQueue();
    }, []);

    const fetchQueue = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/inspection/queue`, { headers });
            setUnits(response.data.data.units);
        } catch (error) {
            console.error('Failed to fetch queue:', error);
        } finally {
            setLoading(false);
        }
    };

    const openDialog = (unit, type) => {
        setSelectedUnit(unit);
        setDialogType(type);
        setNotes('');
        setDamageReason('');
    };

    const closeDialog = () => {
        setSelectedUnit(null);
        setDialogType(null);
        setNotes('');
        setDamageReason('');
    };

    const handleApprove = async () => {
        if (!selectedUnit) return;
        setProcessing(true);
        try {
            await axios.post(`${API_URL}/inspection/${selectedUnit.id}/approve`, { notes }, { headers });
            closeDialog();
            fetchQueue();
        } catch (error) {
            console.error('Failed to approve unit:', error);
            toast.error('Failed to approve unit. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!selectedUnit) return;
        setProcessing(true);
        try {
            await axios.post(`${API_URL}/inspection/${selectedUnit.id}/reject`, { notes, damageReason }, { headers });
            closeDialog();
            fetchQueue();
        } catch (error) {
            console.error('Failed to reject unit:', error);
            toast.error('Failed to reject unit. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">{t('returnsInspection')}</h1>
                        <p className="text-muted-foreground">{t('processReturns')}</p>
                    </div>
                    <Button variant="outline" onClick={fetchQueue}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        {t('refresh')}
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4 text-purple-500" />
                                {t('pendingInspection')}
                            </CardDescription>
                            <CardTitle className="text-3xl text-purple-500">{units.length}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-sm text-muted-foreground">{t('itemsInQuarantine')}</div>
                        </CardContent>
                    </Card>
                </div>

                {/* Quarantine Queue */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Package className="h-5 w-5" />
                            {t('quarantineQueue')}
                        </CardTitle>
                        <CardDescription>{t('quarantineQueueSubtitle')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {units.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                <p className="text-lg">{t('noQuarantineItems')}</p>
                                <p className="text-sm">{t('quarantineEmpty')}</p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('serial')}</TableHead>
                                        <TableHead>{t('product')}</TableHead>
                                        <TableHead>{t('category')}</TableHead>
                                        <TableHead>{t('order')}</TableHead>
                                        <TableHead>{t('customer')}</TableHead>
                                        <TableHead>{t('returnedAt')}</TableHead>
                                        <TableHead className="text-right">{t('actions')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {units.map((unit) => (
                                        <TableRow key={unit.id}>
                                            <TableCell className="font-mono text-xs">{unit.serialNumber}</TableCell>
                                            <TableCell>
                                                <div>
                                                    <div className="font-medium">{unit.productName}</div>
                                                    <div className="text-xs text-muted-foreground">{unit.productSku}</div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">{unit.category}</TableCell>
                                            <TableCell className="font-mono text-xs">{unit.orderNumber}</TableCell>
                                            <TableCell>{unit.customerName}</TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {formatDate(unit.returnedAt)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="text-green-600 border-green-600 hover:bg-green-600/10"
                                                        onClick={() => openDialog(unit, 'approve')}
                                                    >
                                                        <CheckCircle className="h-4 w-4 mr-1" />
                                                        {t('approve')}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="text-red-600 border-red-600 hover:bg-red-600/10"
                                                        onClick={() => openDialog(unit, 'reject')}
                                                    >
                                                        <XCircle className="h-4 w-4 mr-1" />
                                                        {t('reject')}
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                {/* Approve Dialog */}
                <Dialog open={dialogType === 'approve'} onOpenChange={() => closeDialog()}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-green-600">
                                <CheckCircle className="h-5 w-5" />
                                {t('approveItem')}
                            </DialogTitle>
                            <DialogDescription>
                                {t('approveDescription')}
                            </DialogDescription>
                        </DialogHeader>
                        {selectedUnit && (
                            <div className="py-4">
                                <div className="bg-muted p-3 rounded-lg mb-4">
                                    <p className="font-medium">{selectedUnit.productName}</p>
                                    <p className="text-sm text-muted-foreground">{t('serial')}: {selectedUnit.serialNumber}</p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="notes">{t('notesOptional')}</Label>
                                    <Input
                                        id="notes"
                                        placeholder={t('inspectionNotes')}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={closeDialog} disabled={processing}>
                                {t('cancel')}
                            </Button>
                            <Button
                                className="bg-green-600 hover:bg-green-700"
                                onClick={handleApprove}
                                disabled={processing}
                            >
                                {processing ? t('processing') : t('confirmApproval')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Reject Dialog */}
                <Dialog open={dialogType === 'reject'} onOpenChange={() => closeDialog()}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-red-600">
                                <XCircle className="h-5 w-5" />
                                {t('rejectItem')}
                            </DialogTitle>
                            <DialogDescription>
                                {t('rejectDescription')}
                            </DialogDescription>
                        </DialogHeader>
                        {selectedUnit && (
                            <div className="py-4 space-y-4">
                                <div className="bg-muted p-3 rounded-lg">
                                    <p className="font-medium">{selectedUnit.productName}</p>
                                    <p className="text-sm text-muted-foreground">{t('serial')}: {selectedUnit.serialNumber}</p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="damageReason">{t('damageReason')}</Label>
                                    <Input
                                        id="damageReason"
                                        placeholder={t('damageReasonPlaceholder')}
                                        value={damageReason}
                                        onChange={(e) => setDamageReason(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="rejectNotes">{t('notesOptional')}</Label>
                                    <Input
                                        id="rejectNotes"
                                        placeholder={t('inspectionNotes')}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={closeDialog} disabled={processing}>
                                {t('cancel')}
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleReject}
                                disabled={processing}
                            >
                                {processing ? t('processing') : t('confirmRejection')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
